// ─── src/components/nav/MobileDrawerAccordion.jsx ──────────────────────────────
// Mobile-optimized accordion drawer that preserves navigation hierarchy
// Replaces flattened list with expandable sections for mega menus

import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabaseClient";
import { useTheme } from "../../theme/ThemeContext";
import { navigateTo } from "../../utils/navigate";

const NU = "var(--font-body)";
const GD = "var(--font-heading-primary)";

// Map nav_action to handler functions.
// In-app targets route through navigateTo() (pushState, no document reload).
// External urls and open_new_tab still get real navigations, see utils/navigate.js.
function resolveHandler(item, handlers) {
  const { nav_action, url, open_new_tab } = item;
  if (nav_action === "about")              return () => handlers.onNavigateAbout?.();
  if (nav_action === "planning")           return () => handlers.onNavigateStandard?.();
  if (nav_action === "aura-discovery")     return () => navigateTo("/discovery/aura");
  if (nav_action === "browse")             return () => navigateTo("/venue");
  if (nav_action === "the-edit")           return () => navigateTo("/lwd-edit");
  if (nav_action === "join")               return () => navigateTo("/join");
  if (nav_action === "apply-for-inclusion") return () => navigateTo("/apply-for-inclusion");
  // Legacy alias, keeps stale nav_items DB rows routing correctly through
  // the migration window. Safe to remove once admin sweeps the values.
  if (nav_action === "list-your-business") return () => navigateTo("/apply-for-inclusion");
  if (nav_action === "contact")            return () => navigateTo("/contact");
  if (nav_action === "artistry-awards")    return () => navigateTo("/artistry-awards");
  if (url) return () => navigateTo(url, { newTab: open_new_tab });
  return null;
}

export default function MobileDrawerAccordion({
  navItems,
  onClose,
  darkMode,
  onToggleDark,
  handlers
}) {
  const C = useTheme();
  const [expandedId, setExpandedId] = useState(null);
  const [nestedItems, setNestedItems] = useState({}); // Cache: { parentId: [items] }
  const [loading, setLoading] = useState({}); // Loading state per item
  const [touchActive, setTouchActive] = useState(null); // Track touch/active state

  // Expand mega menu or dropdown item
  const expandItem = (item) => {
    if (expandedId === item.id) {
      // Collapse if already expanded
      setExpandedId(null);
      return;
    }

    // Manual mega menu, always use children array from tree (may be empty, shows "No items")
    const sourceTable = item.mega_menu_source;
    if (!sourceTable || sourceTable === "manual") {
      if (item.sections) {
        // Flatten sections into a list with section-header separators
        const flat = [];
        item.sections.forEach(sec => {
          flat.push({ id: `__sec__${sec.label}`, label: sec.label, _isSectionHeader: true });
          sec.items.forEach(cat => flat.push(cat));
        });
        setNestedItems(prev => ({ ...prev, [item.id]: flat }));
      } else {
        setNestedItems(prev => ({ ...prev, [item.id]: item.children || [] }));
      }
      setExpandedId(item.id);
      return;
    }

    // If already cached, just expand
    if (nestedItems[item.id]) {
      setExpandedId(item.id);
      return;
    }

    // Fetch from Supabase table (e.g. magazine_categories)
    setLoading(prev => ({ ...prev, [item.id]: true }));
    supabase
      .from(sourceTable)
      .select("*")
      .order("position", { ascending: true })
      .then(({ data, error }) => {
        if (data && !error) {
          setNestedItems(prev => ({ ...prev, [item.id]: data }));
          setExpandedId(item.id);
        }
        setLoading(prev => ({ ...prev, [item.id]: false }));
      });
  };

  const handleNestedItemClick = (item) => {
    const handler = resolveHandler(item, handlers);
    if (handler) {
      handler();
      onClose?.();
    } else if (item.url) {
      // Fallback: direct URL navigation for items whose nav_action isn't mapped
      navigateTo(item.url);
      onClose?.();
    }
  };

  // Separate CTA items for bottom positioning
  const nonCtaItems = navItems.filter(i => i.type !== "cta");
  const ctaItems = navItems.filter(i => i.type === "cta");

  return (
    <>
      {/* ─ Main nav items section ─ */}
      <nav
        aria-label="Menu sections"
        style={{ padding: "16px 0", flex: 1, overflowY: "auto" }}
      >
        {nonCtaItems.map((item) => {
          const isMega = item.type === "mega_menu" || item.type === "dropdown";
          const isExpanded = expandedId === item.id;
          const isLoading = loading[item.id];
          const nested = nestedItems[item.id] || [];
          const handler = resolveHandler(item, handlers);

          return (
            <div key={item.id}>
              {/* ─ Top-level item ─ */}
              <button
                aria-expanded={isMega ? isExpanded : undefined}
                aria-controls={isMega ? `accordion-${item.id}` : undefined}
                aria-label={isMega ? `${item.label}, expandable menu` : item.label}
                onClick={() => {
                  if (isMega) {
                    expandItem(item);
                  } else {
                    onClose?.();
                    handler?.();
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  background: isExpanded || touchActive === item.id ? "rgba(211,154,33,0.08)" : "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "16px 28px",
                  fontSize: 13,
                  fontWeight: isMega && isExpanded ? 600 : 300,
                  color: isExpanded || touchActive === item.id ? C.gold : "rgba(245,240,232,0.7)",
                  fontFamily: NU,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  transition: "all 0.25s ease",
                  minHeight: 52,
                }}
                onMouseEnter={e => {
                  if (!isExpanded) {
                    e.currentTarget.style.color = C.gold;
                    e.currentTarget.style.background = "rgba(211,154,33,0.06)";
                  }
                }}
                onMouseLeave={e => {
                  if (!isExpanded && touchActive !== item.id) {
                    e.currentTarget.style.color = "rgba(245,240,232,0.7)";
                    e.currentTarget.style.background = "none";
                  }
                }}
                onTouchStart={() => setTouchActive(item.id)}
                onTouchEnd={() => setTouchActive(null)}
              >
                <span>{item.label}</span>
                {isMega && (
                  <span
                    style={{
                      fontSize: 12,
                      opacity: 0.6,
                      transition: "transform 0.3s ease",
                      transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    ▾
                  </span>
                )}
              </button>

              {/* ─ Nested mega menu items (accordion) ─ */}
              {isMega && isExpanded && (
                <div
                  id={`accordion-${item.id}`}
                  role="region"
                  aria-label={`${item.label} submenu`}
                  style={{
                    maxHeight: isExpanded ? "1000px" : "0px",
                    overflow: "hidden",
                    transition: "max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    background: "rgba(211,154,33,0.04)",
                    borderLeft: `2px solid ${C.gold}`,
                    marginLeft: "28px",
                  }}
                >
                  {isLoading ? (
                    <div
                      style={{
                        padding: "16px 28px",
                        fontSize: 12,
                        color: "rgba(245,240,232,0.4)",
                        textAlign: "center",
                      }}
                    >
                      Loading…
                    </div>
                  ) : nested.length > 0 ? (
                    nested.map(nestedItem =>
                      nestedItem._isSectionHeader ? (
                        <div
                          key={nestedItem.id}
                          style={{
                            padding: "10px 28px 6px 40px",
                            fontFamily: NU,
                            fontSize: 8,
                            fontWeight: 700,
                            letterSpacing: "0.22em",
                            textTransform: "uppercase",
                            color: "rgba(211,154,33,0.55)",
                            borderBottom: "1px solid rgba(211,154,33,0.08)",
                            marginBottom: 2,
                          }}
                        >
                          {nestedItem.label}
                        </div>
                      ) : (
                        <button
                          key={nestedItem.id}
                          aria-label={`${item.label}: ${nestedItem.label}`}
                          onClick={() => handleNestedItemClick(nestedItem)}
                          style={{
                            display: "block",
                            width: "100%",
                            textAlign: "left",
                            background: touchActive === nestedItem.id ? "rgba(211,154,33,0.04)" : "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "12px 28px",
                            paddingLeft: "40px",
                            fontSize: 11,
                            fontWeight: 300,
                            color: touchActive === nestedItem.id ? C.gold : "rgba(245,240,232,0.5)",
                            fontFamily: NU,
                            letterSpacing: "0.18em",
                            textTransform: "uppercase",
                            transition: "color 0.2s, background 0.2s",
                            minHeight: 44,
                            borderLeft: touchActive === nestedItem.id ? `2px solid ${C.gold}` : "2px solid transparent",
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.color = C.gold;
                            e.currentTarget.style.background = "rgba(211,154,33,0.04)";
                            e.currentTarget.style.borderLeftColor = C.gold;
                          }}
                          onMouseLeave={e => {
                            if (touchActive !== nestedItem.id) {
                              e.currentTarget.style.color = "rgba(245,240,232,0.5)";
                              e.currentTarget.style.background = "none";
                              e.currentTarget.style.borderLeftColor = "transparent";
                            }
                          }}
                          onTouchStart={() => setTouchActive(nestedItem.id)}
                          onTouchEnd={() => setTouchActive(null)}
                        >
                          {nestedItem.label}
                        </button>
                      )
                    )
                  ) : (
                    <div
                      style={{
                        padding: "12px 28px",
                        fontSize: 12,
                        color: "rgba(245,240,232,0.3)",
                        fontStyle: "italic",
                      }}
                    >
                      No items
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* ─ CTA items section (pinned at bottom) ─ */}
      {ctaItems.length > 0 && (
        <>
          <div style={{ height: 1, background: "rgba(211,154,33,0.12)", margin: "8px 24px" }} />
          <nav aria-label="Primary actions" style={{ padding: "12px 24px 24px" }}>
            {ctaItems.map(item => {
              const handler = resolveHandler(item, handlers);
              const isOutline = item.cta_style === "outline";
              const isDark = item.cta_style === "dark";

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    onClose?.();
                    handler?.();
                  }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "center",
                    fontFamily: NU,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "1.2px",
                    textTransform: "uppercase",
                    color: isDark ? C.gold : isOutline ? C.gold : "#0f0d0a",
                    background: isDark ? "#0a0906" : isOutline ? "transparent" : `linear-gradient(135deg, ${C.gold}, #e8c97a)`,
                    border: `1px solid ${isDark ? "#333" : C.gold}`,
                    borderRadius: 4,
                    padding: "13px 20px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    marginBottom: item === ctaItems[ctaItems.length - 1] ? 0 : 8,
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.opacity = "0.88";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        </>
      )}

      {/* ─ Dark mode toggle (always at bottom) ─ */}
      {!ctaItems.length && <div style={{ height: 1, background: "rgba(211,154,33,0.12)", margin: "8px 24px" }} />}
      <div style={{ padding: "20px 24px" }}>
        <button
          onClick={() => onToggleDark?.()}
          aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(245,240,232,0.5)",
            fontFamily: NU,
            fontSize: 13,
            padding: 0,
            minHeight: 44,
            transition: "color 0.2s",
          }}
          onMouseEnter={e => (e.currentTarget.style.color = C.gold)}
          onMouseLeave={e => (e.currentTarget.style.color = "rgba(245,240,232,0.5)")}
        >
          <span style={{ fontSize: 16 }}>{darkMode ? "☀" : "☽"}</span>
          <span>{darkMode ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </div>
    </>
  );
}
